import "react-phone-number-input/style.css";
import PhoneInput, {
  isValidPhoneNumber,
  parsePhoneNumber,
  PhoneInputProps,
} from "react-phone-number-input";
import PhoneInputWithoutCountrySelect from "react-phone-number-input/input";
import "./styles/PromineoPhoneInput.css";
import { Validator } from "devextreme-react";
import { CustomRule, RequiredRule } from "devextreme-react/validator";
import { useEffect, useState } from "react";
import ControlErrorDisplay from "../ControlErrorDisplay";

export interface PromineoPhoneInputProps
  extends Omit<PhoneInputProps, "onChange"> {
  onPhoneNumberChanged?: (args: PhoneInputValueChangedEventArg) => void;
  onChange?: (args: string) => void;
  withoutCountrySelect?: boolean;
  immediateValidation?: boolean;
  validation?: boolean;
}

export interface PhoneInputValueChangedEventArg {
  countryCode: string;
  nationalNumber: string;
  number: string;
}

export default function PromineoPhoneInput(props: PromineoPhoneInputProps) {
  const {
    withoutCountrySelect,
    onPhoneNumberChanged,
    value,
    required,
    validation,
    immediateValidation,
    onChange,
    ...rest
  } = props;

  const [isValid, setIsValid] = useState();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [defaultCountry] = useState("NO");
  // Used to validate number.
  const [phoneInputValue, setPhoneInputValue] = useState<string>("");

  const callBacks: any = [];

  useEffect(() => {
    setPhoneInputValue(value);
  }, [value]);

  const handleOnChange = (value: any) => {
    const parsedData = value && parsePhoneNumber(value);
    let formattedData: PhoneInputValueChangedEventArg | undefined = undefined;
    setPhoneInputValue(value);
    if (parsedData) {
      formattedData = {
        countryCode: `+${parsedData.countryCallingCode}`,
        nationalNumber: parsedData.nationalNumber,
        number: parsedData.number,
      };
    }

    if (parsedData && formattedData) {
      onPhoneNumberChanged?.(formattedData);
    }

    onChange?.(value);
  };

  const validatePhoneInputOnBlur = () => {
    if (immediateValidation) {
      // Devextreme support suggested way to validate third party control immediately.
      // Keep a callBacks array. Assign that array to validationRequestsCallbacks in adaptar configuration.
      // And then, fire callBacks when we want to validate the control. We are validating on control's onBlur event.
      callBacks.forEach((func: any) => {
        func();
      });
    }
  };

  const adapterConfig = {
    getValue: () => {
      return phoneInputValue;
    },
    applyValidationResults: (e: any) => {
      setIsValid(e.isValid);
      let message = "";
      if (e.brokenRules?.length) {
        message = e.brokenRules[0].message;
      }
      setErrorMessage(message);
    },
    validationRequestsCallbacks: callBacks,
  };

  return (
    <>
      {withoutCountrySelect ? (
        <PhoneInputWithoutCountrySelect
          defaultCountry={defaultCountry}
          placeholder="+154564654"
          {...rest}
          value={phoneInputValue}
          required={required}
          onChange={handleOnChange}
          className="promineo-phone-input input-without-country-select"
        />
      ) : (
        <PhoneInput
          international={true}
          defaultCountry={defaultCountry}
          placeholder="+154564654"
          {...rest}
          value={value}
          required={required}
          onChange={handleOnChange}
          className="promineo-phone-input"
          onBlur={validatePhoneInputOnBlur}
        />
      )}

      {!isValid && errorMessage && (
        <ControlErrorDisplay message={errorMessage} />
      )}
      <Validator adapter={adapterConfig}>
        {required && <RequiredRule message="Phone is required." />}
        {validation !== false && (
          <CustomRule
            message="Invalid phone number."
            type="custom"
            validationCallback={(changedValue: any) => {
              // TODO: Investigate why do we need changedValue param.
              // Note: This will not work if we remove the changedValue param.
              return phoneInputValue && isValidPhoneNumber(phoneInputValue);
            }}
          />
        )}
      </Validator>
    </>
  );
}
